import { useSortable } from '@dnd-kit/sortable'
import { SortableItem } from './SortableItem'
import { Box, IconButton, Skeleton, Stack, SvgIcon } from '@mui/material'
import { IconGripVertical, IconTrash } from '@tabler/icons-react'
import { CSS } from '@dnd-kit/utilities'
import { ModelPhoto } from '../types'

export const SortablePhoto = ({
  placeId,
  photo,
  url,
  onDelete,
  disabled,
}: {
  placeId: number
  photo: ModelPhoto
  url: string | null
  onDelete: (photo: ModelPhoto) => void
  disabled?: boolean
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: placeId,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <SortableItem
      ref={setNodeRef}
      withOpacity={isDragging}
      style={style}
      sx={{
        pointerEvents: disabled ? 'none' : 'auto',
        border: '1px solid',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        flexGrow: 1,
        overflow: 'hidden',
        position: 'relative',
        '&:hover .model-photos-actions': {
          display: 'flex',
          opacity: 1,
          pointerEvents: 'auto',
        },
      }}
    >
      <Box sx={{ position: 'relative', width: 1, paddingTop: '100%' }}>
        {disabled ? (
          <Skeleton
            variant="rectangular"
            sx={{ position: 'absolute', top: 0, left: 0, width: 1, height: 1, zIndex: 1 }}
          />
        ) : null}
        <Box
          component="img"
          src={url ?? undefined}
          alt={`Model photo ${photo.index}`}
          sx={{
            opacity: disabled ? 0.1 : 1,
            position: 'absolute',
            top: 0,
            left: 0,
            width: 1,
            height: 1,
          }}
        />
      </Box>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        className="model-photos-actions"
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          top: 0,
          left: 0,
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: 'none',
          opacity: 0,
          backgroundColor: (t) => t.palette.common.white,
          transition: 'opacity 0.2s ease',
        }}
      >
        <IconButton
          disabled={disabled}
          size="small"
          sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          disableRipple
          {...listeners}
          {...attributes}
        >
          <SvgIcon fontSize="small">
            <IconGripVertical />
          </SvgIcon>
        </IconButton>
        <IconButton onClick={() => onDelete(photo)} disabled={disabled} size="small">
          <SvgIcon fontSize="small">
            <IconTrash />
          </SvgIcon>
        </IconButton>
      </Stack>
    </SortableItem>
  )
}
